import React, { useState } from 'react'
import EditIcon from '@material-ui/icons/Edit'

import moment from 'config/moment'
import { ERRORS, STRINGS } from 'consts'
import { useApi } from 'hooks'
import { snackbar } from 'components'
import { compareStrings, createDateSort } from 'utils'

import { getCoupons } from './api'
import { getActiveTags } from '../Tags/api'
import { getMemberships } from '../Memberships/api'
import { DefaultTable } from '../components'
import { GetCouponsResponse, Coupon } from './types'
import { CreateCouponDialog, DeleteCouponDialog, UpdateCouponDialog } from './components'
import { GetTagsResponse, Tag } from '../Tags/types'
import { GetMembershipsResponse, Membership } from '../Memberships/types'

const onRenderTags = (item: Coupon) => {
  if (!item.tags || !item.tags.length) return ''
  const tags = item.tags.map((t: Tag) => t.title)
  return tags.join(', ')
}

const columns = [
  { title: 'ID', field: 'id' },
  { title: 'Orden', field: 'order' },
  { title: 'Título', field: 'title' },
  { title: 'Descripción corta', field: 'accentDescription' },
  {
    title: 'Descripción larga',
    field: 'description',
    render: (rowData: Coupon) => rowData.description ?? STRINGS.TABLE_COLUMN_NOT_DEFINED,
    customSort: (a: Coupon, b: Coupon) => compareStrings(a.description, b.description),
  },
  { title: 'Texto adicional', field: 'additionalText' },
  {
    title: 'Icono',
    render: (rowData: Coupon) => <img src={rowData.iconUrl} alt="" style={{ width: 20 }} />,
    sorting: false,
  },
  {
    title: 'Imagen',
    render: (rowData: Coupon) => <img src={rowData.imageUrl} alt="" style={{ width: 100 }} />,
    sorting: false,
  },
  {
    title: 'Vencimiento',
    field: 'expiryDate',
    render: (rowData: Coupon) => (
      <table>
        <tbody>
          <tr>
            <td
              style={{
                color: moment().isAfter(rowData.expiryDate, 'day') ? 'red' : 'black',
              }}
            >
              {moment(rowData.expiryDate).format(STRINGS.DATE_FORMAT)}
            </td>
          </tr>
        </tbody>
      </table>
    ),
    customSort: createDateSort<Coupon>(row => row.expiryDate),
  },
  {
    title: 'Tags',
    render: (rowData: Coupon) => onRenderTags(rowData),
    sorting: false,
  },
  {
    title: 'Tags requeridos',
    render: (rowData: Coupon) => (rowData.allTagsAreRequired ? 'Sí' : 'No'),
    sorting: false,
  },
]

const getCouponsResponseGetter = (responseData: GetCouponsResponse) => responseData?.coupons ?? []
const getTagsResponseGetter = (responseData: GetTagsResponse) => responseData?.tags ?? []
const getMembershipsResponseGetter = (responseData: GetMembershipsResponse) =>
  responseData?.memberships ?? []

const Coupons: React.FC = () => {
  const [{ data: coupons, isLoading }, refetchCoupons, setCoupons] = useApi<
    GetCouponsResponse,
    Coupon[]
  >(getCoupons, getCouponsResponseGetter, {
    baseData: [],
    onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
  })

  const [{ data: tags }] = useApi<GetTagsResponse, Tag[]>(getActiveTags, getTagsResponseGetter, {
    baseData: [],
    onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
  })

  const [{ data: memberships }] = useApi<GetMembershipsResponse, Membership[]>(
    getMemberships,
    getMembershipsResponseGetter,
    {
      baseData: [],
      onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
    },
  )

  const [isCreateCouponDialogOpen, setIsCreateCouponDialogOpen] = useState(false)
  const [isDeleteCouponDialogOpen, setIsDeleteCouponDialogOpen] = useState(false)
  const [couponToDelete, setCouponToDelete] = useState<Coupon | null>(null)
  const [isUpdateCouponDialogOpen, setIsUpdateCouponDialogOpen] = useState(false)
  const [couponToUpdate, setCouponToUpdate] = useState<Coupon | null>(null)

  const handleCreateCouponDone = () => {
    refetchCoupons()
    setIsCreateCouponDialogOpen(false)
  }

  const handleDeleteCouponDialogClose = () => {
    setIsDeleteCouponDialogOpen(false)
    setCouponToDelete(null)
  }

  const handleDeleteCouponDone = () => {
    if (!couponToDelete) return
    setCoupons(coupons.filter(coupon => coupon.id !== couponToDelete.id))
    handleDeleteCouponDialogClose()
  }

  const handleUpdateCouponDialogClose = () => {
    refetchCoupons()
    setIsUpdateCouponDialogOpen(false)
    setCouponToUpdate(null)
  }

  const handleUpdateCouponDone = (updatedCoupon: Coupon) => {
    setCoupons(
      coupons.map(coupon => {
        if (coupon.id === updatedCoupon.id) {
          return updatedCoupon
        }
        return coupon
      }),
    )
    handleUpdateCouponDialogClose()
  }

  const actions = [
    {
      icon: 'add',
      tooltip: 'Nueva promo',
      isFreeAction: true,
      onClick: () => setIsCreateCouponDialogOpen(true),
    },
    {
      icon: () => <EditIcon />,
      tooltip: 'Editar promo',
      onClick: (_: React.SyntheticEvent, row: Coupon | Coupon[]) => {
        setCouponToUpdate(row as Coupon)
        setIsUpdateCouponDialogOpen(true)
      },
    },
    {
      icon: 'delete',
      tooltip: `Eliminar`,
      onClick: (_: React.SyntheticEvent, row: Coupon | Coupon[]) => {
        setCouponToDelete(row as Coupon)
        setIsDeleteCouponDialogOpen(true)
      },
    },
  ]

  return (
    <>
      <DefaultTable
        title="Promos"
        columns={columns}
        data={coupons}
        isLoading={isLoading}
        actions={actions}
      />
      {isCreateCouponDialogOpen && (
        <CreateCouponDialog
          handleClose={() => setIsCreateCouponDialogOpen(false)}
          onDone={handleCreateCouponDone}
          tags={tags}
          memberships={memberships}
        />
      )}
      {isDeleteCouponDialogOpen && couponToDelete && (
        <DeleteCouponDialog
          handleClose={handleDeleteCouponDialogClose}
          couponId={couponToDelete.id}
          couponTitle={couponToDelete.title}
          onDone={handleDeleteCouponDone}
        />
      )}
      {isUpdateCouponDialogOpen && couponToUpdate && (
        <UpdateCouponDialog
          handleClose={handleUpdateCouponDialogClose}
          couponToUpdate={couponToUpdate}
          onDone={handleUpdateCouponDone}
          tags={tags}
          memberships={memberships}
        />
      )}
    </>
  )
}

export { Coupons }
